import type { ViewInstance } from 'react-native';
/**
 * SummaryView Component
 * Displays the AI-generated paper summary with all sections
 * Shows skeleton loading state when summary is not yet available
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SummaryViewProps } from './types';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from "../../lib/fonts";
import SummaryFieldWithCitations from './SummaryFieldWithCitations';
import LimitationsWithCitations from './LimitationsWithCitations';
import LaTeXText from '../LaTeXText';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

export default function SummaryView({
    paper,
    summary,
    showSkeleton = false,
    showPaperTitle = true,
    showContents = true,
}: SummaryViewProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const [hoveredSection, setHoveredSection] = React.useState<string | null>(null);
    const [tooltipText, setTooltipText] = React.useState<string | null>(null);

    const sectionRefs = React.useRef<{ [key: string]: ViewInstance | null }>({});

    const sections = [
        { id: 'eli5', title: msg({ id: 'summary.section.eli5', message: "Explain Like I'm Five" }), icon: 'child-care' },
        { id: 'overview', title: msg({ id: 'summary.section.overview', message: 'Overview' }), icon: 'article' },
        { id: 'research-question', title: msg({ id: 'summary.section.researchQuestion', message: 'Research Question' }), icon: 'help-outline' },
        { id: 'methodology', title: msg({ id: 'summary.section.methodology', message: 'Methodology' }), icon: 'science' },
        { id: 'results', title: msg({ id: 'summary.section.results', message: 'Results' }), icon: 'assessment' },
        { id: 'limitations', title: msg({ id: 'summary.section.limitations', message: 'Limitations' }), icon: 'bug-report' },
        { id: 'takeaway', title: msg({ id: 'summary.section.takeaway', message: 'Takeaway' }), icon: 'lightbulb' },
        { id: 'conflicts', title: msg({ id: 'summary.section.conflicts', message: 'Conflicts of Interest' }), icon: 'warning-amber' },
    ];

    const handleTOCClick = (sectionId: string) => {
        if (Platform.OS === 'web') {
            const viewRef = sectionRefs.current[sectionId];
            if (viewRef) {
                const element = viewRef as unknown as HTMLElement;
                if (element && element.scrollIntoView) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }
    };

    const displaySummary = !showSkeleton && summary;

    return (
        <>
            {showPaperTitle ? (
                <View style={styles.paperTitleSection}>
                    <Text style={[styles.paperTitle, fontsLoaded && interSemiBold]}>
                        {paper.title}
                    </Text>
                </View>
            ) : null}

            {showContents ? (
                <View style={styles.tocSection}>
                    <Text style={[styles.tocTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.contents" comment="UI copy in components/papers/SummaryView.tsx">
                        Contents
                    </Trans></Text>
                    <View style={styles.tocList}>
                        {sections.map((section, index) => (
                            <TouchableOpacity
                                key={index}
                                style={styles.tocItem}
                                onPress={() => handleTOCClick(section.id)}
                                activeOpacity={0.7}
                            >
                                <MaterialIcons name={section.icon as any} size={18} color={colors.action} style={styles.tocItemIcon} />
                                <Text style={[styles.tocItemText, fontsLoaded && interMedium]}>
                                    {i18n._(section.title)}
                                </Text>
                                <MaterialIcons name="chevron-right" size={16} color="#9ca3af" />
                            </TouchableOpacity>
                        ))}
                    </View>
                </View>
            ) : null}

            {/* Explain Like I'm Five */}
            <View ref={(ref) => { sectionRefs.current['eli5'] = ref; }} nativeID="eli5" style={[styles.section, hoveredSection === 'eli5' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.explainLikeIMFive" comment="UI copy in components/papers/SummaryView.tsx">
                    Explain Like I'm Five
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <View style={styles.paperzillaTitleContainer}>
                            <Text style={[styles.paperzillaTitleLabel, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.paperzillaTitle" comment="UI copy in components/papers/SummaryView.tsx">
                                Paperzilla title
                            </Trans></Text>
                            <Text style={[styles.paperzillaTitleText, fontsLoaded && interSemiBold]}>
                                {summary.fields?.title?.content || paper.title}
                            </Text>
                        </View>
                        <SummaryFieldWithCitations
                            field={summary.fields?.eli5}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'eli5' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'eli5' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={styles.paperzillaTitleContainer}>
                            <Text style={[styles.paperzillaTitleLabel, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.paperzillaTitle" comment="UI copy in components/papers/SummaryView.tsx">
                                Paperzilla title
                            </Trans></Text>
                            <View style={[styles.skeleton, { width: '90%', height: 24, marginBottom: 4 }]} />
                            <View style={[styles.skeleton, { width: '60%', height: 24 }]} />
                        </View>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '95%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '75%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Overview */}
            <View ref={(ref) => { sectionRefs.current['overview'] = ref; }} nativeID="overview" style={[styles.section, hoveredSection === 'overview' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.overview" comment="UI copy in components/papers/SummaryView.tsx">
                    Overview
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.overview}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'overview' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'overview' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '95%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '88%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '92%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '85%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Research Question */}
            <View ref={(ref) => { sectionRefs.current['research-question'] = ref; }} nativeID="research-question" style={[styles.section, hoveredSection === 'research-question' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.researchQuestion" comment="UI copy in components/papers/SummaryView.tsx">
                    Research Question
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.research_question}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'research-question' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'research-question' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '92%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '85%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Methodology */}
            <View ref={(ref) => { sectionRefs.current['methodology'] = ref; }} nativeID="methodology" style={[styles.section, hoveredSection === 'methodology' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.methodology" comment="UI copy in components/papers/SummaryView.tsx">
                    Methodology
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.methodology}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'methodology' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'methodology' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '95%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '88%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '92%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '80%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Results */}
            <View ref={(ref) => { sectionRefs.current['results'] = ref; }} nativeID="results" style={[styles.section, hoveredSection === 'results' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.results" comment="UI copy in components/papers/SummaryView.tsx">
                    Results
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.results}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'results' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'results' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '95%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '88%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '92%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '85%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Limitations */}
            <View ref={(ref) => { sectionRefs.current['limitations'] = ref; }} nativeID="limitations" style={[styles.section, hoveredSection === 'limitations' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.limitations" comment="UI copy in components/papers/SummaryView.tsx">
                    Limitations
                </Trans></Text>
                {displaySummary ? (
                    <LimitationsWithCitations
                        limitations={summary.limitations}
                        fontsLoaded={fontsLoaded}
                        onHoverChange={(isHovered, text) => {
                            setHoveredSection(isHovered ? 'limitations' : null);
                            setTooltipText(isHovered && text ? text : null);
                        }}
                    />
                ) : (
                    [1, 2, 3].map(i => (
                        <View key={i} style={styles.limitationItem}>
                            <View style={[styles.skeleton, { width: 120, height: 16, marginBottom: 8 }]} />
                            <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 6 }]} />
                            <View style={[styles.skeleton, { width: '90%', height: 20, marginBottom: 6 }]} />
                            <View style={[styles.skeleton, { width: '75%', height: 20 }]} />
                        </View>
                    ))
                )}
            </View>

            {/* Takeaway */}
            <View ref={(ref) => { sectionRefs.current['takeaway'] = ref; }} nativeID="takeaway" style={[styles.section, hoveredSection === 'takeaway' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.takeaway" comment="UI copy in components/papers/SummaryView.tsx">
                    Takeaway
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.takeaway}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'takeaway' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'takeaway' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '92%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '85%', height: 20 }]} />
                    </>
                )}
            </View>

            {/* Conflicts of Interest */}
            <View ref={(ref) => { sectionRefs.current['conflicts'] = ref; }} nativeID="conflicts" style={[styles.section, hoveredSection === 'conflicts' && styles.sectionElevated]}>
                <Text style={[styles.sectionTitle, fontsLoaded && interSemiBold]}><Trans id="papers.summaryview.conflictsOfInterest" comment="UI copy in components/papers/SummaryView.tsx">
                    Conflicts of Interest
                </Trans></Text>
                {displaySummary ? (
                    <>
                        <SummaryFieldWithCitations
                            field={summary.fields?.coi}
                            fontsLoaded={fontsLoaded}
                            style={styles.sectionContent}
                            onHoverChange={(isHovered, text) => {
                                setHoveredSection(isHovered ? 'conflicts' : null);
                                setTooltipText(isHovered && text ? text : null);
                            }}
                        />
                        {hoveredSection === 'conflicts' && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.sectionTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <View style={[styles.skeleton, { width: '100%', height: 20, marginBottom: 8 }]} />
                        <View style={[styles.skeleton, { width: '80%', height: 20 }]} />
                    </>
                )}
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    paperTitleSection: {
        marginBottom: 24,
    },
    paperTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: colors.text900,
        lineHeight: 32,
    },
    tocSection: {
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: colors.border,
    },
    tocTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
    },
    tocList: {
        marginTop: 8,
    },
    tocItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 4,
        borderRadius: 8,
        marginBottom: 4,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: colors.primary100,
            },
        }),
    },
    tocItemIcon: {
        marginRight: 8,
        width: 20,
    },
    tocItemText: {
        flex: 1,
        fontSize: 15,
        color: colors.text700,
        fontWeight: '500',
    },
    section: {
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: colors.border,
        position: 'relative',
    },
    sectionElevated: {
        zIndex: 1000,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
    },
    sectionContent: {
        fontSize: 15,
        color: colors.text700,
        lineHeight: 22,
    },
    skeleton: {
        backgroundColor: '#f3f4f6',
        borderRadius: 4,
    },
    paperzillaTitleContainer: {
        marginBottom: 16,
        marginTop: 8,
    },
    paperzillaTitleLabel: {
        fontSize: 12,
        fontWeight: '500',
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    paperzillaTitleText: {
        fontSize: 18,
        fontWeight: '700',
        color: colors.text900,
        lineHeight: 26,
    },
    limitationItem: {
        marginBottom: 16,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
    },
    sectionTooltip: {
        position: 'relative',
        left: 0,
        right: 0,
        backgroundColor: colors.text900,
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
        zIndex: 99999999,
        pointerEvents: 'none',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        } as any),
    },
    tooltipText: {
        fontSize: 14,
        color: '#ffffff',
        lineHeight: 20,
    },
});
